import type {
  AdBriefAssetFieldsFragment,
  AdCardAssetFieldsFragment,
} from "src/graphql";

/** A resize as the brief page's fragment selects it, with the same version history the primary carries. */
export type BriefResizeRow = NonNullable<
  AdBriefAssetFieldsFragment["ad_brief"]
>["resizes"][number];

/**
 * The fields every resize carries: the same as any ad asset. Its `id` is what
 * endpoints accept for a resize, whichever table it lives in. A resize is an
 * `ad_brief_assets` row in the primary's brief with a
 * `source_ad_brief_asset_version_id`; a video primary's resizes may also be
 * `ad_brief_asset_size_variants` rows adapted into this shape.
 */
export type ResizeFields = Pick<
  AdCardAssetFieldsFragment,
  | "id"
  | "asset_type"
  | "svg"
  | "asset_url"
  | "video_composition"
  | "component_assets"
  | "channel_size_id"
  | "channel_size"
>;

/**
 * A legacy `ad_brief_asset_size_variants` row standing in as a resize. It is
 * its own write target: `id` names the row that ratings, comments and rasters
 * are stored against.
 */
export type LegacyResize = ResizeFields & {
  isLegacy: true;
};

/** A resize that is an `ad_brief_assets` row. Writes go against its current version. */
export type SizeAssetResize<T extends ResizeFields = ResizeFields> = T & {
  isLegacy: false;
};

/**
 * `isLegacy` says which rows back the resize. `T` is the size-asset row as the
 * caller's fragment selects it, so a richer selection keeps its extra fields.
 */
export type Resize<T extends ResizeFields = ResizeFields> =
  | LegacyResize
  | SizeAssetResize<T>;

export type LegacySizeVariantInput = {
  id: string;
  asset_type: string;
  svg?: string | null;
  asset_url?: string | null;
  video_composition?: unknown;
  size_key?: string | null;
  channel_size_id?: string | null;
  channel_size?: ResizeFields["channel_size"];
};

export type ResizesOfAsset<T extends ResizeFields = ResizeFields> = {
  asset_type?: string | null;
  video_composition?: unknown;
  ad_brief_asset_size_variants?: readonly LegacySizeVariantInput[] | null;
  ad_brief?: { resizes?: readonly T[] | null } | null;
};

export function hasVideoContent(
  asset: Pick<ResizesOfAsset, "asset_type" | "video_composition">,
): boolean {
  return asset.asset_type === "video" || asset.video_composition != null;
}

/**
 * Whether `asset`'s resizes are read from the brief's size assets alone. A
 * video primary may still carry legacy rows, so its readers go through
 * `resizesOf` rather than one table.
 */
export function readsSizeAssets(
  asset: Pick<ResizesOfAsset, "asset_type" | "video_composition">,
): boolean {
  return !hasVideoContent(asset);
}

/**
 * The resizes of `asset`: the brief's derived assets, newest first. A video
 * primary's legacy rows follow, newest row per slot, at the channel sizes no
 * derived asset occupies, since a slot with a derived asset renders from it.
 * A legacy row with no channel size has no slot to lose and is always kept.
 */
export function resizesOf<T extends ResizeFields>(
  asset: ResizesOfAsset<T>,
): Resize<T>[] {
  const sizeAssets: Resize<T>[] = sizeAssetResizesOf(asset);
  if (readsSizeAssets(asset)) return sizeAssets;

  const legacyResizes = legacyRowsInOpenSlots(
    sizeAssets,
    newestLegacyRowPerSlot(asset.ad_brief_asset_size_variants ?? []),
  ).map(legacyRowAsResize);
  return [...sizeAssets, ...legacyResizes];
}

/**
 * Every derived ad in the brief, newest first. Use it where only ads with their
 * own asset count; `resizesOf` also lists a video primary's legacy size rows.
 */
export function sizeAssetResizesOf<T extends ResizeFields>(
  asset: Pick<ResizesOfAsset<T>, "ad_brief">,
): SizeAssetResize<T>[] {
  return (asset.ad_brief?.resizes ?? []).map((resize) => ({
    ...resize,
    isLegacy: false as const,
  }));
}

/**
 * The legacy rows at channel sizes none of `sizeAssets` occupies. A slot with
 * a size asset renders from it, so the rows there are history. A row with no
 * channel size has no slot to lose and is kept.
 */
export function legacyRowsInOpenSlots<
  R extends Pick<LegacySizeVariantInput, "channel_size_id">,
>(
  sizeAssets: readonly Pick<ResizeFields, "channel_size_id">[],
  legacyRows: readonly R[],
): R[] {
  const occupiedSlots = new Set(
    sizeAssets.flatMap((resize) =>
      resize.channel_size_id === null ? [] : [resize.channel_size_id],
    ),
  );
  return legacyRows.filter(
    (row) =>
      row.channel_size_id == null || !occupiedSlots.has(row.channel_size_id),
  );
}

/**
 * Legacy rows are one per save, and the fragment lists them newest first, so
 * the first row seen for a slot is that slot's current render.
 */
function newestLegacyRowPerSlot(
  rows: readonly LegacySizeVariantInput[],
): LegacySizeVariantInput[] {
  const seenSlots = new Set<string>();
  return rows.filter((row) => {
    const slot = row.channel_size_id ?? row.size_key ?? null;
    if (slot === null) return true;
    if (seenSlots.has(slot)) return false;
    seenSlots.add(slot);
    return true;
  });
}

function legacyRowAsResize(row: LegacySizeVariantInput): LegacyResize {
  return {
    isLegacy: true,
    id: row.id,
    asset_type: row.asset_type,
    svg: row.svg ?? null,
    // The asset schema stores markup with no raster preview as an empty URL.
    asset_url: row.asset_url ?? "",
    video_composition: row.video_composition ?? null,
    // Legacy rows have no map of their own; they render through the primary's.
    component_assets: null,
    channel_size_id: row.channel_size_id ?? null,
    channel_size: row.channel_size ?? null,
  };
}
